"use professional"
import { useState, useEffect } from "react"
import Select from 'react-select'
import { useAuth } from "../../AuthContext"
import { useNavigate } from "react-router-dom"

function ProjectForm({ existingProject = null, onSubmitSuccess }) {
  const [projectCount, setProjectCount] = useState(existingProject ? 1 : 0)
  const [projects, setProjects] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [technologyOptions, setTechnologyOptions] = useState([])
  const [customTechInputs, setCustomTechInputs] = useState({})
  const { user } = useAuth()
  const navigate = useNavigate()
  const proId = user?.secondId || 0

  // Remove all data:image/ prefix logic; just return the base64 string
  const decodeImage = (base64String) => {
    if (!base64String) return ""
    return base64String
  }

  useEffect(() => {
    fetch("http://localhost:5000/skills")
      .then(response => response.json())
      .then(data => {
        const formattedOptions = data.map(skill => ({
          value: skill.skill_id,
          label: skill.skill_name.charAt(0).toUpperCase() + skill.skill_name.slice(1)
        }))
        formattedOptions.push({ value: "__other__", label: "Other" })
        setTechnologyOptions(formattedOptions)
      })
      .catch(error => console.error("Error fetching skills:", error))
  }, [])

  useEffect(() => {
    if (existingProject) {
      let skillsUsed = []
      if (typeof existingProject.skills_used === "string") {
        try {
          skillsUsed = JSON.parse(existingProject.skills_used)
        } catch (error) {
          console.error("Error parsing skills_used:", error)
        }
      }
      const formattedCompletionDate = existingProject.completion_date
        ? new Date(existingProject.completion_date).toISOString().split("T")[0]
        : ""
      const mappedProject = {
        title: existingProject.project_title || "",
        description: existingProject.project_description || "",
        completionDate: formattedCompletionDate,
        technologies: skillsUsed.map(skill => ({
          value: skill.id,
          label: skill.name,
        })),
        currentTech: "",
        projectType: existingProject.type || "",
        customProjectType: existingProject.type === "other" ? existingProject.type : "",
        projectPrice: existingProject.project_price || "",
        encodedImages: [
          existingProject.encoded_image1,
          existingProject.encoded_image2,
          existingProject.encoded_image3,
          existingProject.encoded_image4,
          existingProject.encoded_image5,
        ].filter(Boolean).map(decodeImage),
      }
      setProjects([mappedProject])
    } else {
      setProjects(
        Array(projectCount)
          .fill()
          .map(() => ({
            title: "",
            description: "",
            completionDate: "",
            technologies: [],
            currentTech: "",
            projectType: "",
            customProjectType: "",
            projectPrice: "",
            encodedImages: [],
          }))
      )
    }
  }, [existingProject, projectCount])

  const handleProjectCountChange = (e) => {
    const count = Number.parseInt(e.target.value)
    setProjectCount(count)
    setProjects(
      Array(count)
        .fill()
        .map(() => ({
          title: "",
          description: "",
          completionDate: "",
          technologies: [],
          currentTech: "",
          projectType: "",
          customProjectType: "",
          projectPrice: "",
          encodedImages: [],
        }))
    )
  }

  const handleProjectChange = (index, field, value) => {
    const updatedProjects = [...projects]
    updatedProjects[index][field] = value
    setProjects(updatedProjects)
  }

  // Add technology with "Other" support
  const addTechnology = (index) => {
    const updatedProjects = [...projects]
    const { currentTech, technologies } = updatedProjects[index]
    if (currentTech && currentTech.value === "__other__") {
      const customTech = customTechInputs[index]?.trim()
      if (customTech && !technologies.some(t => t.label.toLowerCase() === customTech.toLowerCase())) {
        updatedProjects[index].technologies = [
          ...technologies,
          { value: "__custom__", label: customTech, isCustom: true }
        ]
        updatedProjects[index].currentTech = ""
        setProjects(updatedProjects)
        setCustomTechInputs({ ...customTechInputs, [index]: "" })
      }
      return
    }
    if (currentTech && !technologies.some(t => t.value === currentTech.value)) {
      updatedProjects[index].technologies = [...technologies, currentTech]
      updatedProjects[index].currentTech = ""
      setProjects(updatedProjects)
    }
  }

  const removeTechnology = (index, tech) => {
    const updatedProjects = [...projects]
    updatedProjects[index].technologies = updatedProjects[index].technologies.filter((t) => t !== tech)
    setProjects(updatedProjects)
  }

  const handleImageUpload = (index, e) => {
    const files = Array.from(e.target.files)
    const updatedProjects = [...projects]
    files.forEach((file) => {
      // Only accept .JPG files (case-insensitive)
      if (!file.name.toLowerCase().endsWith('.jpg')) {
        alert("Only .JPG files are allowed.")
        return
      }
      // Enforce 100KB size limit
      if (file.size > 102400) {
        alert("Each image must not exceed 100KB.")
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        // Only use the base64 string, no prefix
        const base64String = reader.result.split(",")[1]
        updatedProjects[index].encodedImages.push(base64String)
        setProjects([...updatedProjects])
      }
      reader.readAsDataURL(file)
    })
  }

  const removeEncodedImage = (projectIndex, imageIndex) => {
    const updatedProjects = [...projects]
    updatedProjects[projectIndex].encodedImages.splice(imageIndex, 1)
    setProjects(updatedProjects)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const formattedProjects = projects.map((project) => {
      const customTechnologies = project.technologies.filter(t => t.isCustom)
      const existingTechnologies = project.technologies.filter(t => !t.isCustom)
      return {
        title: project.title,
        description: project.description,
        completionDate: project.completionDate,
        technologies: existingTechnologies.map(tech => tech.value),
        customTechnologies: customTechnologies.map(tech => tech.label),
        projectType: project.projectType === "other" ? project.customProjectType : project.projectType,
        projectPrice: project.projectPrice,
        encodedImages: project.encodedImages,
      }
    })
    const url = existingProject
      ? `http://localhost:5000/update-project/${existingProject.professional_project_id}`
      : `http://localhost:5000/projects/${proId}`
    const method = existingProject ? "PUT" : "POST"
    const body = existingProject
      ? JSON.stringify(formattedProjects[0])
      : JSON.stringify({ projects: formattedProjects })
    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body,
      })
      const data = await response.json()
      if (data.error) {
        alert("Error submitting projects: " + data.error)
      } else {
        alert(existingProject ? "Project updated successfully!" : "Projects submitted successfully!")
        if (!existingProject) {
          setProjectCount(0)
          setProjects([])
        }
        onSubmitSuccess && onSubmitSuccess(data)
        navigate("/ProProject")
      }
    } catch (error) {
      console.error("Error submitting projects:", error)
      alert("An error occurred while submitting the projects. Please try again.")
    }
  }

  const inputStyle = {
    background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)',
    borderRadius: '10px', padding: '10px 14px', width: '100%', outline: 'none', fontSize: '0.9rem',
    transition: 'border-color 0.2s',
  };
  const labelStyle = { display: 'block', color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: 600, marginBottom: '6px' };

  const selectStyles = {
    control: (base) => ({ ...base, background: 'var(--bg-elevated)', borderColor: 'var(--border-subtle)', borderRadius: '10px', minHeight: '42px', boxShadow: 'none', '&:hover': { borderColor: '#10b981' } }),
    menu: (base) => ({ ...base, background: 'var(--bg-card)', border: '1px solid var(--border-glass)', borderRadius: '10px' }),
    option: (base, state) => ({ ...base, background: state.isFocused ? 'var(--bg-elevated)' : 'transparent', color: 'var(--text-primary)', cursor: 'pointer' }),
    singleValue: (base) => ({ ...base, color: 'var(--text-primary)' }),
    input: (base) => ({ ...base, color: 'var(--text-primary)' }),
    placeholder: (base) => ({ ...base, color: 'var(--text-muted)' }),
  };

  return (
    <div style={{ maxWidth: '720px', margin: '40px auto', background: 'var(--bg-card)', borderRadius: '20px', border: '1px solid var(--border-glass)', padding: '40px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #10b981, var(--accent-cyan))' }} />
      <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-display)', textAlign: 'center', marginBottom: '6px' }}>
        {existingProject ? "Edit Your Project" : "Add Your Projects"}
      </h1>
      <p style={{ color: 'var(--text-muted)', textAlign: 'center', marginBottom: '28px', fontSize: '0.9rem' }}>
        {existingProject
          ? "Update your project details to keep them up-to-date."
          : "Share your projects to showcase your skills and expertise."}
      </p>
      <form onSubmit={handleSubmit} className="grid gap-6">
        {!existingProject && (
          <div className="mb-4">
            <label style={labelStyle} htmlFor="projectCount">How many projects do you want to add?</label>
            <input style={inputStyle} id="projectCount" type="number" placeholder="Enter number of projects" min="1" required value={projectCount} onChange={handleProjectCountChange}
              onFocus={e => e.target.style.borderColor = '#10b981'} onBlur={e => e.target.style.borderColor = 'var(--border-subtle)'} />
          </div>
        )}
        {projects.map((project, index) => (
          <div key={index} style={{ padding: '20px', border: '1px solid var(--border-glass)', borderRadius: '14px', background: 'var(--bg-glass)', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '16px' }}>Project {index + 1}</h3>
            <div className="mb-4">
              <label style={labelStyle} htmlFor={`projectTitle${index}`}>Project Title</label>
              <input style={inputStyle} id={`projectTitle${index}`} type="text" placeholder="Enter project title" required value={project.title}
                onChange={(e) => handleProjectChange(index, 'title', e.target.value)}
                onFocus={e => e.target.style.borderColor = '#10b981'} onBlur={e => e.target.style.borderColor = 'var(--border-subtle)'} />
            </div>
            <div className="mb-4">
              <label style={labelStyle} htmlFor={`projectDescription${index}`}>Project Description</label>
              <textarea style={{ ...inputStyle, resize: 'vertical' }} id={`projectDescription${index}`} placeholder="Briefly describe your project" maxLength="500" rows="4" required value={project.description}
                onChange={(e) => handleProjectChange(index, 'description', e.target.value)}
                onFocus={e => e.target.style.borderColor = '#10b981'} onBlur={e => e.target.style.borderColor = 'var(--border-subtle)'} />
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}><span>{project.description.length}</span>/500 characters</p>
            </div>
            <div className="mb-4">
              <label style={labelStyle} htmlFor={`projectCompletionDate${index}`}>Completion Date</label>
              <input style={inputStyle} id={`projectCompletionDate${index}`} type="date" required value={project.completionDate}
                onChange={(e) => handleProjectChange(index, 'completionDate', e.target.value)}
                onFocus={e => e.target.style.borderColor = '#10b981'} onBlur={e => e.target.style.borderColor = 'var(--border-subtle)'} />
            </div>
            <div className="mb-4">
              <label style={labelStyle} htmlFor={`projectTech${index}`}>Technology and Skills Used</label>
              <div>
                <Select options={technologyOptions.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()))}
                  value={project.currentTech} onChange={(selectedOption) => handleProjectChange(index, 'currentTech', selectedOption)}
                  styles={selectStyles} className="w-full" />
                {project.currentTech && project.currentTech.value === "__other__" && (
                  <input type="text" placeholder="Enter custom technology" value={customTechInputs[index] || ""}
                    onChange={e => setCustomTechInputs({ ...customTechInputs, [index]: e.target.value })}
                    style={{ ...inputStyle, marginTop: '8px' }}
                    onFocus={e => e.target.style.borderColor = '#10b981'} onBlur={e => e.target.style.borderColor = 'var(--border-subtle)'} />
                )}
                <button type="button" onClick={() => addTechnology(index)}
                  style={{ marginTop: '8px', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: '10px', fontWeight: 600, cursor: 'pointer' }}>
                  Add Technology
                </button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {project.technologies.map((tech) => (
                  <span key={tech.value + tech.label} style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 12px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600, background: 'rgba(16,185,129,0.08)', color: '#10b981', border: '1px solid rgba(16,185,129,0.15)' }}>
                    {tech.label}
                    <button type="button" onClick={() => removeTechnology(index, tech)} style={{ marginLeft: '6px', color: 'rgba(16,185,129,0.5)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>×</button>
                  </span>
                ))}
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor={`projectType${index}`} style={labelStyle}>Project Type</label>
              <select id={`projectType${index}`} name={`projectType${index}`} value={project.projectType}
                onChange={(e) => handleProjectChange(index, 'projectType', e.target.value)} style={inputStyle}>
                <option value="">Select project type</option>
                <option value="web">Web Development</option>
                <option value="mobile">Mobile Development</option>
                <option value="data">Data Analysis</option>
                <option value="ml">Machine Learning</option>
                <option value="other">Other</option>
              </select>
              {project.projectType === "other" && (
                <input type="text" placeholder="Enter custom project type" value={project.customProjectType}
                  onChange={(e) => handleProjectChange(index, 'customProjectType', e.target.value)}
                  style={{ ...inputStyle, marginTop: '8px' }}
                  onFocus={e => e.target.style.borderColor = '#10b981'} onBlur={e => e.target.style.borderColor = 'var(--border-subtle)'} />
              )}
            </div>
            <div className="mb-4">
              <label htmlFor={`projectPrice${index}`} style={labelStyle}>Project Price</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '0.85rem' }}>$</span>
                <input type="number" name={`projectPrice${index}`} id={`projectPrice${index}`} placeholder="Enter project price"
                  value={project.projectPrice} onChange={(e) => handleProjectChange(index, 'projectPrice', e.target.value)}
                  style={{ ...inputStyle, paddingLeft: '28px' }}
                  onFocus={e => e.target.style.borderColor = '#10b981'} onBlur={e => e.target.style.borderColor = 'var(--border-subtle)'} />
              </div>
            </div>
            <div className="mb-4">
              <label style={labelStyle} htmlFor={`encodedImages${index}`}>Upload Project Images</label>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'block', marginBottom: '8px' }}>
                Only <b>.JPG</b> images allowed. Max <b>100KB</b> each.
              </span>
              <input id={`encodedImages${index}`} type="file" multiple accept=".jpg" onChange={(e) => handleImageUpload(index, e)}
                style={{ ...inputStyle, padding: '8px 14px' }} />
              <div className="mt-2 flex flex-wrap gap-2">
                {project.encodedImages.map((image, imageIndex) => (
                  <div key={imageIndex} style={{ position: 'relative' }}>
                    <img src={`data:image/jpeg;base64,${decodeImage(image)}`} alt={`Encoded ${imageIndex}`}
                      style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '10px', border: '1px solid var(--border-subtle)' }} />
                    <button type="button" onClick={() => removeEncodedImage(index, imageIndex)}
                      style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#ef4444', color: '#fff', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', fontSize: '0.7rem' }}>
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
        <div className="flex items-center justify-between">
          <button type="submit"
            style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none', padding: '12px 32px', borderRadius: '10px', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem', transition: 'opacity 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
            {existingProject ? "Update Project" : "Add Projects"}
          </button>
        </div>
      </form>
    </div>
  )
}

export default ProjectForm